import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { ConfirmDialogComponent, DEFAULT_SNACKBAR_CONFIG, Logger } from 'src/app/@shared';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { OfferTag, OfferTagService } from '../..';
import { ActivatedRoute } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

const log = new Logger('OfferTagsComponent');

@Component({
  selector: 'app-offer-tags',
  templateUrl: './offer-tags.component.html',
  styleUrls: ['./offer-tags.component.scss']
})
export class OfferTagsComponent<T extends OfferTag> implements OnInit {

  public get offerTagService(): OfferTagService<T> {
    return this._offerTagService;
  }
  public set offerTagService(value: OfferTagService<T>) {
    this._offerTagService = value;
  }

  viewModel$ = combineLatest([
    this.offerTagService.offerTags$,
    this.offerTagService.isLoading$,
    this.offerTagService.totalRecords$,
    this.offerTagService.page$,
  ]).pipe(
    map(([offerTags, isLoading, totalRecords, page]) => {
      if (offerTags.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0
        page.pageIndex = 0
        this.offerTagService.page(page);
        this.offerTagService.reload();
      }
      return { offerTags, isLoading, totalRecords, page }
    }),
  );
  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'OfferTagName', 'ClientKey', 'Actions'];
  offerTagList: OfferTag[] = [];
  selection = new SelectionModel<OfferTag>(true, []);
  constructor(private _offerTagService: OfferTagService<T>, private mediaObserver: MediaObserver, private route: ActivatedRoute, private dialog: MatDialog,
    private matSnackBar: MatSnackBar) { }

  ngOnInit(): void {
    log.debug('init');
    this.offerTagService.offerTags$.subscribe((data) => {
      this.offerTagList = data;
    })
    this.clearSearch();
    this.offerTagService.reload();

    this.route.queryParams.subscribe(queryParams => {
      let searchKey = queryParams['search'];

      if (searchKey) {
        this._offerTagService.search(searchKey)
        this.filtersForm.controls.search.setValue(searchKey);
      } else {
        this._offerTagService.search('');
        this.filtersForm.controls.search.setValue('');
      }
    });

    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'OfferTagName', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'OfferTagName', 'ClientKey', 'Actions'];
        }
      });
  }

  onSearch(event: any) {
    this.offerTagService.search(event.target.value);
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.offerTagService.search('');
  }


  filtersForm = new FormGroup({
    search: new FormControl<string>(''),
  });

  onSort(sortState: Sort): void {
    this.offerTagService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.offerTagService.page(pageEvent);
  }
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.offerTagList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.offerTagList.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  deleteSelectedRecords() {
    let selectedRecords: any;   
    selectedRecords = this.getSelectedSectionRecords();    
    if (selectedRecords && selectedRecords.length > 0) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: `Do you want to remove Offer Tag(s)?`,
        },
        disableClose: true,
      });

      confirmDialog.afterClosed().subscribe(
        confirmResult => {
          if (confirmResult) {
            this.offerTagService.deleteOfferTags(selectedRecords).subscribe({
              next: () => {
                this.matSnackBar.open(`Offer Tag(s) deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
                this.offerTagService.reload();               
                  this.clearSelection();                
              },
              error: (error) => {
                log.error('Error in deleting Offer Tag', error);

                if (error.error.value) {
                  throw new Error(error.error.value);
                } else {
                  throw new Error(error.message);
                }
              }
            });
          }
        });
    }
  }

  downloadJSON(){
    this.offerTagService.downloadOfferTagsJSON();
  }

  downloadCSV(){
    this.offerTagService.downloadOfferTagsCSV();
  }

}
